import type { 
  LabelField, 
  LabelFixedField, 
  LabelLayout, 
  LabelGeneratorConfig, 
  TextRenderOptions,
  LabelExportOptions 
} from './types'

export class LabelGenerator {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private config: LabelGeneratorConfig
  
  constructor(canvas: HTMLCanvasElement, config: LabelGeneratorConfig) {
    this.canvas = canvas
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      throw new Error('No se pudo obtener el contexto 2D del canvas')
    }
    this.ctx = ctx
    this.config = config
  }

  /**
   * Conversión de milímetros a píxeles
   */
  private mmToPx(mm: number): number {
    return Math.round((mm * this.config.dpi) / 25.4)
  }

  /**
   * Convertir texto a title case (primera letra de cada palabra en mayúscula)
   */
  private toTitleCase(text: string): string {
    return text.replace(/\w\S*/g, (txt) => {
      return txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    })
  }

  /**
   * Determinar el layout basado en los campos disponibles
   */
  static determineLayout(fields: LabelField[], fixedField?: LabelFixedField): LabelLayout {
    const enabledFields = fields.filter(field => field.value?.trim())
    const hasQR = Boolean(fixedField?.value?.trim())
    
    // Simular el campo QR como LabelField para compatibilidad
    const qrField: LabelField | undefined = fixedField ? {
      name: 'qr_code',
      label: fixedField.label || 'QR Code',
      value: fixedField.value,
      type: 'text'
    } : undefined

    // Los primeros 2 campos van al header
    const headerFields = enabledFields.slice(0, 2)
    // Del 3er campo en adelante son extras
    const extraFields = enabledFields.slice(2)

    // Determinar tipo de layout
    let layoutType: LabelLayout['type']
    
    if (headerFields.length <= 2 && !hasQR && extraFields.length === 0) {
      layoutType = 'simple'
    } else if (headerFields.length <= 2 && hasQR && extraFields.length === 0) {
      layoutType = 'text-qr'
    } else if (hasQR && (headerFields.length > 0 || extraFields.length > 0)) {
      layoutType = 'header-fields-qr'
    } else if (!hasQR && headerFields.length >= 2 && extraFields.length > 0) {
      layoutType = 'header-fields'
    } else {
      layoutType = 'fields-only'
    }

    return {
      type: layoutType,
      headerFields,
      extraFields,
      fixedField: qrField,
      hasQR
    }
  }

  /**
   * Generar QR code como imagen usando la librería qrcode
   */
  static async generateQRCode(value: string): Promise<string> {
    try {
      // Usar la librería qrcode real
      const QRCode = await import('qrcode')
      return await QRCode.default.toDataURL(value, {
        width: 120,
        margin: 1,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      })
    } catch (error) {
      console.error('Error generando QR code:', error)
      // Fallback: crear QR dummy si falla la librería
      return new Promise((resolve) => {
        const qrCanvas = document.createElement('canvas')
        qrCanvas.width = 120
        qrCanvas.height = 120
        const qrCtx = qrCanvas.getContext('2d')
        
        if (!qrCtx) {
          resolve('data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg==')
          return
        }
        
        // Fondo blanco
        qrCtx.fillStyle = 'white'
        qrCtx.fillRect(0, 0, 120, 120)
        
        // Borde negro
        qrCtx.strokeStyle = 'black'
        qrCtx.lineWidth = 2
        qrCtx.strokeRect(2, 2, 116, 116)
        
        // Texto del código
        qrCtx.fillStyle = 'black'
        qrCtx.font = 'bold 10px Arial'
        qrCtx.textAlign = 'center'
        qrCtx.fillText('QR CODE', 60, 30)
        qrCtx.font = '8px Arial'
        qrCtx.fillText(value, 60, 45)
        
        // Patrón QR simplificado
        for (let i = 0; i < 8; i++) {
          for (let j = 0; j < 8; j++) {
            if (Math.random() > 0.5) {
              qrCtx.fillRect(10 + i * 12, 55 + j * 8, 8, 8)
            }
          }
        }
        
        resolve(qrCanvas.toDataURL())
      })
    }
  }



  /**
   * Dibujar texto con auto-resize y overflow handling
   */
  private drawTextWithAutoResize(
    text: string, 
    x: number, 
    y: number, 
    maxWidth: number, 
    maxHeight: number, 
    initialFontSize: number, 
    options: TextRenderOptions = {},
    forceFontSize?: number
  ): void {
    const {
      align = 'center',
      baseline = 'top',
      bold = false,
      uppercase = false,
      maxLines = 999
    } = options

    this.ctx.textAlign = align
    this.ctx.textBaseline = baseline
    
    const processedText = uppercase ? text.toUpperCase() : text
    let fontSize = initialFontSize
    let lines: string[] = []
    
    // Función para dividir texto en líneas
    const getLines = (currentFontSize: number) => {
      this.ctx.font = `${bold ? 'bold ' : ''}${Math.max(currentFontSize, 10)}px Arial`
      const words = processedText.split(' ')
      const result: string[] = []
      let currentLine = words[0] || ''

      for (let i = 1; i < words.length; i++) {
        const testLine = currentLine + ' ' + words[i]
        const metrics = this.ctx.measureText(testLine)
        if (metrics.width > maxWidth) {
          result.push(currentLine)
          currentLine = words[i]
        } else {
          currentLine = testLine
        }
      }
      if (currentLine) result.push(currentLine)
      return result
    }

    // Usar tamaño forzado si se especifica, o calcular automáticamente
    if (forceFontSize) {
      fontSize = forceFontSize
      lines = getLines(fontSize)
    } else {
      // Reducir fuente hasta que quepa
      do {
        lines = getLines(fontSize)
        const totalHeight = lines.length * (fontSize * 1.2)
        if (totalHeight <= maxHeight && lines.every(line => {
          this.ctx.font = `${bold ? 'bold ' : ''}${Math.min(fontSize, 200)}px Arial`
          return this.ctx.measureText(line).width <= maxWidth
        })) {
          break
        }
        fontSize -= 1
      } while (fontSize > 8)
    }

    // Truncar líneas si exceden maxLines
    const finalLines = lines.slice(0, maxLines)
    let hasOverflow = lines.length > maxLines
    
    // Si hay overflow, agregar "..."
    if (hasOverflow && finalLines.length > 0) {
      this.ctx.font = `${bold ? 'bold ' : ''}${Math.min(fontSize, 200)}px Arial`
      const ellipsis = "..."
      
      let lastLine = finalLines[finalLines.length - 1]
      
      while (this.ctx.measureText(lastLine + ellipsis).width > maxWidth && lastLine.length > 0) {
        lastLine = lastLine.slice(0, -1).trim()
      }
      
      finalLines[finalLines.length - 1] = lastLine + ellipsis
    }

    // Dibujar las líneas
    this.ctx.font = `${bold ? 'bold ' : ''}${Math.min(fontSize, 200)}px Arial`
    this.ctx.fillStyle = 'black'
    
    const lineHeight = fontSize * 1.2
    const startY = baseline === 'middle' ? y - (finalLines.length * lineHeight) / 2 : y

    finalLines.forEach((line, index) => {
      this.ctx.fillText(line, x, startY + (index * lineHeight))
    })
  }

  /**
   * Renderizar layout simple (1-2 campos centrados)
   */
  private renderSimpleLayout(layout: LabelLayout, contentX: number, contentY: number, contentWidth: number, contentHeight: number): void {
    const headerText = layout.headerFields.map(field => field.value).filter(Boolean).join(" ")
    
    if (headerText) {
      const centerX = contentX + contentWidth / 2
      const centerY = contentY + contentHeight / 2
      
      this.drawTextWithAutoResize(
        headerText, 
        centerX, 
        centerY, 
        contentWidth - this.mmToPx(4), 
        contentHeight - this.mmToPx(4), 
        this.mmToPx(5), // Tamaño aumentado
        { align: 'center', baseline: 'middle', bold: true, uppercase: true, maxLines: 3 }
      )
    }
  }

  /**
   * Renderizar layout texto + QR
   */
  private async renderTextQRLayout(layout: LabelLayout, contentX: number, contentY: number, contentWidth: number, contentHeight: number, qrDataUrl?: string): Promise<void> {
    const headerText = layout.headerFields.map(field => field.value).filter(Boolean).join(" ")
    
    // Área del header (5/8 del ancho)
    const headerWidth = Math.floor(contentWidth * 5 / 8)
    const headerX = contentX + headerWidth / 2
    const headerY = contentY + contentHeight / 1.9
    
    if (headerText) {
      this.drawTextWithAutoResize(
        headerText, 
        headerX, 
        headerY, 
        headerWidth - this.mmToPx(2), 
        contentHeight - this.mmToPx(4), 
        this.mmToPx(3.5), // Tamaño reducido para mejor distribución en líneas
        { align: 'center', baseline: 'middle', bold: true, uppercase: true, maxLines: 7 }
      )
    }

    // Área del QR (3/8 del ancho, lado derecho)
    if (qrDataUrl && layout.fixedField) {
      const qrAreaWidth = Math.floor(contentWidth * 3 / 8)
      const qrX = contentX + headerWidth + (qrAreaWidth - this.mmToPx(5) * (this.config.contentScale || 1.0)) / 4
      const qrY = contentY + (contentHeight - this.mmToPx(10) * (this.config.contentScale || 1.0)) / 5
      
      return new Promise((resolve) => {
        const qrImg = new Image()
        qrImg.crossOrigin = 'anonymous'
        qrImg.onload = () => {
          const qrSize = this.mmToPx(15) * (this.config.contentScale || 1.0)
          this.ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize)
          
          // Folio debajo del QR (también escalado)
          this.ctx.font = `bold ${this.mmToPx(2.2) * (this.config.contentScale || 1.0)}px Arial`
          this.ctx.textAlign = 'center'
          this.ctx.fillStyle = 'black'
          this.ctx.fillText(layout.fixedField!.value, qrX + qrSize / 2, qrY + qrSize + this.mmToPx(2) * (this.config.contentScale || 1.0), qrAreaWidth)
          
          resolve()
        }
        qrImg.onerror = () => resolve()
        qrImg.src = qrDataUrl
      })
    }
  }

  /**
   * Renderizar layout header + campos + QR
   */
  private async renderHeaderFieldsQRLayout(layout: LabelLayout, contentX: number, contentY: number, contentWidth: number, contentHeight: number, qrDataUrl?: string): Promise<void> {
    const headerText = layout.headerFields.map(field => field.value).filter(Boolean).join(" ")
    
    // Header toma 3.5/8 de altura y todo el ancho (más espacio)
    const headerHeight = Math.floor(contentHeight * 3.5 / 8)
    const headerX = contentX + contentWidth / 2
    const headerY = contentY + headerHeight / 2
    
    if (headerText) {
      this.drawTextWithAutoResize(
        headerText, 
        headerX, 
        headerY, 
        contentWidth+150, // Márgenes laterales reducidos
        headerHeight - this.mmToPx(0.2), 
        50, // Tamaño aumentado aún más para que el header sea más prominente
        { align: 'center', baseline: 'middle', bold: true, uppercase: true, maxLines: 3 }
      )
    }

    // Área restante (4.5/8 de altura)
    const remainingY = contentY + headerHeight
    const remainingHeight = contentHeight - headerHeight
    
    // QR usa 3/8 del ancho derecho - posicionado más arriba
    const qrAreaWidth = Math.floor(contentWidth * 3 / 8)
    const qrX = contentX + contentWidth - qrAreaWidth + (qrAreaWidth - this.mmToPx(5)) / 1.4
    const qrY = remainingY + this.mmToPx(-3) // Subido para evitar corte del texto
    
    // Campos extra usan 5/8 del ancho izquierdo
    const fieldsAreaWidth = contentWidth - qrAreaWidth
    
    // Dibujar campos extra en líneas separadas con tamaños diferenciados
    if (layout.extraFields.length > 0) {
      const availableHeight = remainingHeight+140
      const totalLines = layout.extraFields.length + 1
      const lineHeight = availableHeight / totalLines+30
      const maxWidth = fieldsAreaWidth + 190 // Más ancho disponible para el texto
      const maxHeight = lineHeight-20
      
      // Tamaños de fuente diferenciados
      const field3FontSize = 60 // Tamaño actual para el 3er campo
      const field4FontSize = this.mmToPx(6) // Un poco más grande para el 4to campo
      const otherFieldsFontSize = this.mmToPx(3.2) // Tamaño normal para campos restantes
      
      layout.extraFields.forEach((field, index) => {
        // Apply title case only if the field has titleCase property set to true
        const fieldText = field.titleCase ? this.toTitleCase(field.value || '-') : (field.value || '-')
        const fieldLineY = remainingY + (index * lineHeight) + lineHeight / 2
        
        // Determinar el tamaño de fuente según el índice del campo
        let fontSize = otherFieldsFontSize
        if (index === 0) { // 3er campo (índice 0 en extraFields)
          fontSize = field3FontSize
        } else if (index === 1) { // 4to campo (índice 1 en extraFields)
          fontSize = field4FontSize
        }
        
        this.drawTextWithAutoResize(
          fieldText, 
          contentX-68, // Márgenes laterales reducidos para campos extra
          fieldLineY - 20, 
          maxWidth,
          maxHeight,
          fontSize,
          { align: 'left', baseline: 'middle', bold: false, uppercase: false, maxLines: 4 }
        )
      })
    }

    // Dibujar QR
    if (qrDataUrl && layout.fixedField) {
      return new Promise((resolve) => {
        const qrImg = new Image()
        qrImg.crossOrigin = 'anonymous'
        qrImg.onload = () => {
          const qrSize = this.mmToPx(20) * (this.config.contentScale || 1.0)
          this.ctx.drawImage(qrImg, qrX-10, qrY-27, qrSize, qrSize)
          
          // Folio debajo del QR (también escalado)
          this.ctx.font = `bold ${this.mmToPx(2.7) * (this.config.contentScale || 1.0)}px Arial`
          this.ctx.textAlign = 'center'
          this.ctx.fillStyle = 'black'
          this.ctx.fillText(layout.fixedField!.value, qrX - 10 + qrSize / 2, qrY + qrSize -35 + this.mmToPx(2) * (this.config.contentScale || 1.0), qrAreaWidth)
          
          resolve()
        }
        qrImg.onerror = () => resolve()
        qrImg.src = qrDataUrl
      })
    }
  }

  /**
   * Renderizar layout header + campos (sin QR)
   */
  private renderHeaderFieldsLayout(layout: LabelLayout, contentX: number, contentY: number, contentWidth: number, contentHeight: number): void {
    const headerText = layout.headerFields.map(field => field.value).filter(Boolean).join(" ")
    
    // Header toma 3.5/8 de altura y todo el ancho (más prominente)
    const headerHeight = Math.floor(contentHeight * 3.5 / 8)
    const headerX = contentX + contentWidth / 2
    const headerY = contentY + headerHeight / 2
    
    if (headerText) {
      this.drawTextWithAutoResize(
        headerText, 
        headerX, 
        headerY, 
        contentWidth - this.mmToPx(2), 
        headerHeight - this.mmToPx(1), 
        this.mmToPx(5), // Tamaño mucho más grande para ser prominente
        { align: 'center', baseline: 'middle', bold: true, uppercase: true, maxLines: 3 }
      )
    }
    
    // Campos extra centrados horizontalmente con label
    const remainingY = contentY + headerHeight
    const remainingHeight = contentHeight - headerHeight
    const fieldHeight = remainingHeight / layout.extraFields.length + 24
    
    layout.extraFields.forEach((field, index) => {
      const fieldY = remainingY + (index * fieldHeight) + fieldHeight / 2
      const fieldCenterX = contentX + contentWidth / 2
      
      // Aplicar title case al valor del campo extra (3er campo en adelante)
      const fieldValue = this.toTitleCase(field.value || '-')
      
      // Formato: "LABEL: Valor" centrado
      const labelText = `${field.label.toUpperCase()}: ${fieldValue}`
      
      this.drawTextWithAutoResize(
        labelText, 
        fieldCenterX, 
        fieldY, 
        contentWidth - this.mmToPx(4), 
        fieldHeight - this.mmToPx(1), 
        this.mmToPx(2),
        { align: 'center', baseline: 'middle', bold: false, uppercase: false, maxLines: 2 }
      )
    })
  }

  /**
   * Renderizar layout solo campos
   */
  private renderFieldsOnlyLayout(layout: LabelLayout, contentX: number, contentY: number, contentWidth: number, contentHeight: number): void {
    const allFields = [...layout.headerFields, ...layout.extraFields]
    const fieldHeight = contentHeight / allFields.length
    
    allFields.forEach((field, index) => {
      const fieldY = contentY + (index * fieldHeight) + fieldHeight / 2
      
      // Aplicar title case a los campos a partir del 3er campo (index >= 2)
      const fieldText = index >= 2 ? this.toTitleCase(field.value || '-') : (field.value || '-')
      
      this.drawTextWithAutoResize(
        fieldText, 
        contentX + this.mmToPx(1), 
        fieldY, 
        contentWidth - this.mmToPx(2), 
        fieldHeight - this.mmToPx(1), 
        this.mmToPx(2.5),
        { align: 'left', baseline: 'middle', bold: false, uppercase: false, maxLines: 2 }
      )
    })
  }

  /**
   * Generar etiqueta completa
   */
  async generateLabel(layout: LabelLayout, qrDataUrl?: string): Promise<string> {
    // Configurar canvas
    const canvasWidth = this.mmToPx(this.config.width)
    const canvasHeight = this.mmToPx(this.config.height)
    const margin = this.mmToPx(this.config.margin)

    this.canvas.width = canvasWidth
    this.canvas.height = canvasHeight

    // Limpiar canvas (fondo blanco)
    this.ctx.fillStyle = 'white'
    this.ctx.fillRect(0, 0, canvasWidth, canvasHeight)

    // Área de contenido base (sin margen)
    const baseContentWidth = canvasWidth - (margin * 2)
    const baseContentHeight = canvasHeight - (margin * 2)
    
    // Aplicar escala al contenido
    const contentScale = this.config.contentScale || 1.0
    const scaledContentWidth = baseContentWidth * contentScale
    const scaledContentHeight = baseContentHeight * contentScale
    
    // Centrar el contenido escalado
    const contentX = margin + (baseContentWidth - scaledContentWidth) / 2
    const contentY = margin + (baseContentHeight - scaledContentHeight) / 2
    
    // Usar dimensiones escaladas para el contenido
    const contentWidth = scaledContentWidth
    const contentHeight = scaledContentHeight

    // Renderizar según el tipo de layout
    switch (layout.type) {
      case 'simple':
        this.renderSimpleLayout(layout, contentX, contentY, contentWidth, contentHeight)
        break
        
      case 'text-qr':
        await this.renderTextQRLayout(layout, contentX, contentY, contentWidth, contentHeight, qrDataUrl)
        break
        
      case 'header-fields-qr':
        await this.renderHeaderFieldsQRLayout(layout, contentX, contentY, contentWidth, contentHeight, qrDataUrl)
        break
        
      case 'header-fields':
        this.renderHeaderFieldsLayout(layout, contentX, contentY, contentWidth, contentHeight)
        break
        
      case 'fields-only':
        this.renderFieldsOnlyLayout(layout, contentX, contentY, contentWidth, contentHeight)
        break
    }

    // Retornar imagen como data URL
    return this.canvas.toDataURL('image/png', 1.0)
  }

  /**
   * Exportar etiqueta en diferentes formatos
   */
  exportLabel(options: LabelExportOptions = { format: 'png' }): string {
    const { format, quality = 1.0, scale = 1 } = options
    
    if (scale !== 1) {
      // Crear canvas escalado
      const scaledCanvas = document.createElement('canvas')
      scaledCanvas.width = this.canvas.width * scale
      scaledCanvas.height = this.canvas.height * scale
      
      const scaledCtx = scaledCanvas.getContext('2d')
      if (scaledCtx) {
        scaledCtx.imageSmoothingEnabled = false
        scaledCtx.drawImage(this.canvas, 0, 0, scaledCanvas.width, scaledCanvas.height)
        return scaledCanvas.toDataURL(`image/${format}`, quality)
      }
    }
    
    return this.canvas.toDataURL(`image/${format}`, quality)
  }
}
